<template>
  <div v-rect-loading="loading" class="password-container">
    <div class="password-editor-header">
      <div class="password-editor-title">{{ $t(`${i18nPrefix}.title.changePassword`) }}</div>
    </div>
    <div class="password-info-container">
      <WrappedFrom
        hideRequiredAsterisk
        ref="form"
        class="password-info"
        label-width="220px"
        :model="passwordInfo"
        :formLines="formLines"
      >
        <template #currentPassword="{ lineName }">
          <el-input
            v-model="passwordInfo[lineName]"
            :type="viewCurrentPassword ? 'text' : 'password'"
          >
            <template v-if="passwordInfo[lineName]" #suffix>
              <i class="password-icon iconfont" :class="viewCurrentPassword ? 'iconVisible' : 'iconHide1'" @click="onViewCurrentPassword"></i>
            </template>
          </el-input>
        </template>
        <template #password="{ lineName }">
          <el-input
            v-model="passwordInfo[lineName]"
            :type="viewPassword ? 'text' : 'password'"
          >
            <template v-if="passwordInfo[lineName]" #suffix>
              <i class="password-icon iconfont" :class="viewPassword ? 'iconVisible' : 'iconHide1'" @click="onViewPassword"></i>
            </template>
          </el-input>
        </template>
        <template #confirmPassword="{ lineName }">
          <el-input
            v-model="passwordInfo[lineName]"
            :type="viewConfirmPassword ? 'text' : 'password'"
          >
            <template v-if="passwordInfo[lineName]" #suffix>
              <i class="password-icon iconfont" :class="viewConfirmPassword ? 'iconVisible' : 'iconHide1'" @click="onViewConfirmPassword"></i>
            </template>
          </el-input>
        </template>
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" type="primary" @click="handleSave">{{ $t('common.btn.submit') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { validatePassword } from '@/utils/validator'
import { passwordEncrypt } from '@/utils/rsa'
import { mixedRouterPush } from '@/router/routes'

export default {
  components: {
    WrappedFrom
  },
  data () {
    return {
      i18nPrefix: 'admin.resetPassword',
      loading: false,
      passwordInfo: {
        currentPassword: null,
        password: null,
        confirmPassword: null
      },
      existPassword: true,
      viewCurrentPassword: false,
      viewPassword: false,
      viewConfirmPassword: false
    }
  },
  computed: {
    formLines () {
      const lines = {
        currentPassword: {
          label: this.$t(`${this.i18nPrefix}.label.currentPassword`),
          hidden: !this.existPassword,
          rules: [
            { required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }
          ],
          contentSlot: 'currentPassword'
        },
        password: {
          label: this.$t(`${this.i18nPrefix}.label.newPassword`),
          rules: [
            { required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' },
            { validator: validatePassword, trigger: 'blur' },
            { validator: this.validateSamePassword, trigger: 'blur' }
          ],
          contentSlot: 'password'
        },
        confirmPassword: {
          label: this.$t(`${this.i18nPrefix}.label.confirmPassword`),
          rules: [
            { required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' },
            { validator: validatePassword, trigger: 'blur' },
            { validator: this.validateSamePassword, trigger: 'blur' }
          ],
          contentSlot: 'confirmPassword'
        }
      }
      return lines
    },
    form () {
      return this.$refs.form
    }
  },
  created () {
    this.loadUserPasswordInfo()
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('userManagement', ['checkPasswordExistence', 'updatePasswordByUser']),
    async loadUserPasswordInfo () {
      this.loading = true
      try {
        const resp = await this.checkPasswordExistence()
        if (resp) {
          this.existPassword = resp.exist_password
        }
      } finally {
        this.loading = false
      }
    },
    async handleSave () {
      const valid = await this.form.submit()
      if (!valid) {
        return
      }
      this.loading = true
      try {
        const passwordInfo = this.passwordInfo
        const resp = await this.updatePasswordByUser({
          old_password: passwordEncrypt(passwordInfo.currentPassword),
          new_password: passwordEncrypt(passwordInfo.password),
          confirm_password: passwordEncrypt(passwordInfo.confirmPassword)
        })
        if (resp) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.updateSuccess`))
          this.passwordInfo = {
            currentPassword: null,
            password: null,
            confirmPassword: null
          }
          this.existPassword = true
          this.handleCancel()
        }
      } finally {
        this.loading = false
      }
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.go(-1)
      }
    },
    async validateSamePassword (rule, value, callback) {
      if (value && value === this.passwordInfo.currentPassword) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.passwordSameAsPrevious`)))
      }
      if (!this.passwordInfo.password || !this.passwordInfo.confirmPassword) {
        callback()
        return
      }
      if (this.passwordInfo.password !== this.passwordInfo.confirmPassword) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.passwordNotSame`)))
      } else {
        callback()
      }
    },
    onViewCurrentPassword () {
      this.viewCurrentPassword = !this.viewCurrentPassword
    },
    onViewPassword () {
      this.viewPassword = !this.viewPassword
    },
    onViewConfirmPassword () {
      this.viewConfirmPassword = !this.viewConfirmPassword
    }
  }
}
</script>

<style lang="scss" scoped>
.password-container {
  width: 100%;
  min-height: 100%;
  background: #FFFFFF;
  min-width: 1320px;
  padding: 20px;
  overflow: auto;

  .password-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .password-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .password-info-container {
    width: 570px;
    margin: 80px auto 0;

    ::v-deep {
      .el-form-item {
        margin-bottom: 30px;
      }
    }

    .password-icon {
      font-size: 12px;
    }

    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;
      margin: 30px auto 0;

      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;

        &:last-child {
          margin-left: 24px;
          margin-right: 0;
          background: #0096cc;
          border-color: #0096cc;
          color: #ffffff;
        }
      }
    }
  }
}
</style>
